import type { CanonicalEnvironment } from "./types";
export type { CanonicalEnvironment } from "./types";

// Known aliases - all lowercase since we normalize (lowercase + trim) before checking
// Sources:
// - @indeed/ops stagingLevels: prod, stage, qa, infra, dev, local
// - IndeedStagingLevel.name(): PRODUCTION, STAGE, QA, ALTQA, E2EQA, INFRA, CONTINUOUS_PROVISIONING, DEVELOPER_PROVISIONING, UNKNOWN
// - IndeedStagingLevel.getId(): prod, stage, qa, altqa, e2eqa, infra, dev, local
// - iOS: prod, qa, e2eqa
// - Android: PROD, QA, E2EQA
const PRODUCTION_ALIASES = new Set(["prod", "production"]);
const QA_ALIASES = new Set([
  "qa",
  "stage",
  "altqa",
  "e2eqa",
  "infra",
  "dev",
  "development",
  "local",
  "continuous_provisioning",
  "developer_provisioning",
]);

/**
 * Normalizes raw build environment values from any platform to canonical values.
 *
 * Process:
 * 1. Normalize the input (lowercase + trim)
 * 2. Check if normalized value is a known alias
 * 3. If not recognized, log to Datadog and return 'prod' (Phase 1)
 *    - Phase 2: throw an error after monitoring confirms no other aliases are being used
 *
 * Returns 'prod' or 'qa' only - all non-production aliases map to 'qa'.
 *
 * @param value - Value from native bridge or web
 * @returns 'prod' or 'qa'
 */
export function normalizeEnvironment(value: string | undefined | null): CanonicalEnvironment {
  if (!value || value.trim() === "") {
    console.error("[EnvironmentError] Invalid buildEnvironment: undefined/null/empty");
    return "prod";
  }

  const normalized = value.toLowerCase().trim();

  if (PRODUCTION_ALIASES.has(normalized)) {
    return "prod";
  }
  if (normalized === "e2eqa") {
    return normalized;
  }
  if (QA_ALIASES.has(normalized)) {
    return "qa";
  }

  // Unknown value - log and fallback for Phase 1
  console.error(
    `[EnvironmentError] Invalid buildEnvironment: "${value}" (normalized: "${normalized}")`,
  );
  return "prod";
}

/**
 * Returns true if the environment is production.
 */
export function isProductionEnvironment(env: CanonicalEnvironment): boolean {
  return env === "prod";
}

export function isE2EQAEnvironment(env: CanonicalEnvironment): boolean {
  return env === "e2eqa";
}
